import { A } from '@ember/array';
import type Enumerable from '@ember/array/-private/enumerable';
import type NativeArray from '@ember/array/-private/native-array';
import Controller from '@ember/controller';
import { action, get, set } from '@ember/object';
import { dependentKeyCompat } from '@ember/object/compat';
import ObjectProxy from '@ember/object/proxy';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import { buildWaiter, waitFor } from '@ember/test-waiters';
import { isEmpty, isBlank, isPresent } from '@ember/utils';
import type { AsyncBelongsTo, AsyncHasMany } from '@ember-data/model';
import type Model from '@ember-data/model';
import type StoreService from '@ember-data/store';
import { tracked } from '@glimmer/tracking';
import { endOfDay, formatISO, startOfDay } from 'date-fns';
import type AbilitiesService from 'ember-can/services/abilities';
import { Changeset } from 'ember-changeset';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { TaskInstance } from 'ember-concurrency';
import { task, timeout, dropTask, restartableTask, all } from 'ember-concurrency';
import config from 'garaje/config/environment';
import type AbstractFlowModel from 'garaje/models/abstract/abstract-flow';
import type AgreementModel from 'garaje/models/agreement';
import type BlacklistFilter from 'garaje/models/blacklist-filter';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DocumentUploadPageModel from 'garaje/models/document-upload-page';
import type EmployeeModel from 'garaje/models/employee';
import type EntryModel from 'garaje/models/entry';
import type EventReportModel from 'garaje/models/event-report';
import FlowModel from 'garaje/models/flow';
import type PlatformJobModel from 'garaje/models/platform-job';
import type PrinterModel from 'garaje/models/printer';
import type { VisualComplianceReport } from 'garaje/models/screener-result';
import type ScreenerResult from 'garaje/models/screener-result';
import type SignInFieldModel from 'garaje/models/sign-in-field';
import type UserDatum from 'garaje/models/user-datum';
import type UserDocumentAttachmentModel from 'garaje/models/user-document-attachment';
import type UserDocumentTemplateAttachmentModel from 'garaje/models/user-document-template-attachment';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import type ActiveStorageService from 'garaje/services/active-storage-extension';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type AuthzService from 'garaje/services/authz';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { TransitionSource } from 'garaje/services/global-routing';
import type GlobalRoutingService from 'garaje/services/global-routing';
import type LoggerService from 'garaje/services/logger';
import type MetricsService from 'garaje/services/metrics';
import type ReactRendererService from 'garaje/services/react-renderer';
import type StateService from 'garaje/services/state';
import type UploaderFactoryService from 'garaje/services/uploader-factory';
import { createBlocklistFilter, saveBlocklistFilter } from 'garaje/utils/blocklist-utils';
import { modifyDateInTimeZone } from 'garaje/utils/date-fns-tz-utilities';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { entryApprovalMessage } from 'garaje/utils/entry-approval-message';
import { NON_ASSIGNABLE_FLOWS, PURPOSE_OF_VISIT } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import MappedField from 'garaje/utils/mapped-field';
import normalizeResponse from 'garaje/utils/normalize-response';
import { refreshApprovalChainAuditLogs } from 'garaje/utils/refresh-approval-chain-audit-logs';
import { resolveReturnToBackLink, type ReturnToBackLink } from 'garaje/utils/return-to-back-link';
import { Permission } from 'garaje/utils/ui-permissions';
import urlBuilder from 'garaje/utils/url-builder';
import addOrUpdateUserData from 'garaje/utils/user-data';
import zft from 'garaje/utils/zero-for-tests';
import _intersection from 'lodash/intersection';
import { reads, notEmpty } from 'macro-decorators';
import moment from 'moment-timezone';
import type { Root } from 'react-dom/client';
import type { ApprovalChainDetailsCard } from 'reactRichComponents/visitors/ApprovalChainDetailsCard';
import { defer } from 'rsvp';
import { cached } from 'tracked-toolbox';

import type { VisitorsEntryModel } from './route';

const EVENT_REPORT_POLLING_TIMEOUT = zft(10000);
const NUMBER_OF_POLLING_TRIES = zft(3);
const testWaiter = buildWaiter('new-visitor-form-component-waiter');
const isTesting = config.environment === 'test';

export type FieldChangeset = DetailedChangeset<Partial<SignInFieldModel> | SignInFieldModel>;

export default class VisitorsEntryController extends Controller {
  queryParams = ['transitionedFrom', 'returnTo', 'returnToModel', 'returnToTab'];

  // ActiveStorage service: https://github.com/algonauti/ember-active-storage
  @service declare activeStorageExtension: ActiveStorageService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare authz: AuthzService;
  @service declare abilities: AbilitiesService;
  @service declare currentAdmin: CurrentAdminService;
  @service declare state: StateService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare store: StoreService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare router: RouterService;
  @service declare globalRouting: GlobalRoutingService;
  @service declare logger: LoggerService;
  @service declare uploaderFactory: UploaderFactoryService;
  @service declare reactRenderer: ReactRendererService;

  private approvalChainDetailsCardRoot?: Root;

  checkStatuses = ['Checked', 'Not checked'];
  @tracked previousEntries = A<EntryModel>([]);
  @tracked displayPrinterSelectorModal = false;
  @tracked morePreviousEntriesUrl: string | null = null;
  @tracked eventReports?: Array<EventReportModel | PlatformJobModel>;
  @tracked userDataChangeset?: FieldChangeset[];
  @tracked staticFields?: MappedField[];
  @tracked confirmRemovalOfDocument: VisitorDocumentModel | null = null;
  @tracked hasAttachments = false;
  @tracked transitionedFrom: TransitionSource = 'location';

  // Optional origin threaded by callers (e.g. the visitor-profile Screeners
  // tab "View" button) so Back returns to where the user came from instead of
  // the entries log. `returnTo` presence switches the back target.
  @tracked returnTo: string | null = null;
  @tracked returnToModel: string | null = null;
  @tracked returnToTab: string | null = null;

  @tracked blocklistFilterInModal?: BlacklistFilter;
  @tracked activeTab: 'visit-details' | 'documents' = 'visit-details';
  @tracked _routeSignInFields: NativeArray<SignInFieldModel> | null = null;

  declare connectedTenants: VisitorsEntryModel['connectedTenants'];
  declare config: VisitorsEntryModel['config'];
  declare agreeableNdas: VisitorsEntryModel['agreeableNdas'];
  declare agreements: AgreementModel[];
  declare printers: VisitorsEntryModel['printers'];
  declare vrSubscription: VisitorsEntryModel['vrSubscription'];
  declare blocklistContacts: VisitorsEntryModel['blocklistContacts'];
  declare idScanContacts: VisitorsEntryModel['idScanContacts'];
  declare model: EntryModel;

  @action
  updateUserDataField(): void {
    /*
     * NOOP -- the way we interact with userData is slightly different
     * here to how we do it invites. Since the code was copy pasted we
     * need to keep this until we refactor the component entry-fields.
     *
     * Intentional TODO: Refactor this.
     */
  }

  @reads('state.currentLocation') currentLocation!: StateService['currentLocation'];
  @reads('hasChanges') canSave!: boolean;
  @reads('flowField.value') flowName!: string;

  get canEditScoped(): boolean {
    if (this.model.anonymizedAt) return false;
    return this.abilities.can('edit entry', this.model);
  }

  get canSignOut(): boolean {
    if (this.model.anonymizedAt) return false;

    if (!this.featureFlags.isEnabled('visitors-disabled-sign-out')) {
      return this.canEditScoped;
    }

    const canSignOutPermission = this.abilities.can('sign-out entry');

    if (this.currentLocation?.visitorsConfiguration?.employeeSignOutDisabled) {
      return canSignOutPermission;
    }
    return this.canEditScoped || canSignOutPermission;
  }

  get canEditUnscoped(): boolean {
    if (this.model.anonymizedAt) return false;
    return this.abilities.can('edit entry');
  }

  get canReview(): boolean {
    return this.abilities.can('review entry-approval', {
      context: 'location',
      visitApproval: this.model.visitApproval,
      parent: this.model,
    });
  }

  get cannotReview(): boolean {
    return !this.canReview;
  }

  get visitorDocsCapableOfAttachments(): VisitorDocumentModel[] {
    return this.visitorDocuments?.filter((doc) => doc.isCapableOfAttachments) ?? [];
  }

  get hasPreRegistrationRequiredReports(): boolean {
    return Boolean(this.model.visitApproval?.preRegistrationRequiredReviewResult);
  }

  get visualComplianceReport(): VisualComplianceReport[] | undefined {
    const model = this.model;

    const visualComplianceResult = model.visitApproval?.visualComplianceResult;
    return visualComplianceResult ? [visualComplianceResult.report] : undefined;
  }

  get noVisualComplianceMatch(): boolean {
    if (!this.model.visitApproval?.visualComplianceResult) return false;
    return !this.model.visitApproval.visualComplianceResult.didFail;
  }

  @notEmpty('model.invite.id') hasInvite!: boolean;
  @reads('flow.activeUserDocumentTemplateConfigurations')
  activeUserDocumentTemplateConfigurations!: FlowModel['activeUserDocumentTemplateConfigurations'];
  @reads('state.currentLocation.timezone', 'America/Los_Angeles') timezone!: string;

  // Document review tab getters

  @cached
  get customUserDocumentTemplateConfigurations(): UserDocumentTemplateConfigurationModel[] {
    // this.flow is typed AbstractFlowModel; at runtime it may also be an
    // EmployeeScreeningFlowModel (sibling class, no documentUploadPage relation).
    // instanceof narrows to FlowModel only — no cast needed afterward.
    const flow = this.flow;
    if (!(flow instanceof FlowModel)) return [];
    const page = flow.belongsTo('documentUploadPage').value() as DocumentUploadPageModel | null;
    if (!page?.enabled) return [];
    return page.userDocumentTemplateConfigurations.slice().filter((c) => c.active);
  }

  get requiredDocumentTemplates(): CustomUserDocumentTemplateModel[] {
    if (!this.abilities.can('access document-upload')) return [];
    const flow = this.flow;
    if (!(flow instanceof FlowModel)) return [];
    const page = flow.belongsTo('documentUploadPage').value() as DocumentUploadPageModel | null;
    if (!page?.enabled) return [];
    return page.customUserDocumentTemplates?.slice() ?? [];
  }

  @cached
  get allVisitorDocuments(): VisitorDocumentModel[] {
    return this.model?.visitorDocuments?.slice() ?? [];
  }

  get customVisitorDocuments(): VisitorDocumentModel[] {
    const templateIds = new Set(this.requiredDocumentTemplates.map((t) => t.id));
    return this.allVisitorDocuments.filter((doc) => templateIds.has(doc.belongsTo('userDocumentTemplate').id()));
  }

  get showDocumentsTab(): boolean {
    if (!this.abilities.can('access document-upload')) return false;
    return this.requiredDocumentTemplates.length > 0 || this.customVisitorDocuments.length > 0;
  }

  get hasDocumentsPendingReview(): boolean {
    return this.customVisitorDocuments.some((doc) => doc.isReview);
  }

  get documentRequirementsSummary(): Array<{
    template: { title: string };
    isUploaded: boolean;
    statusLabel: string | null;
  }> {
    const docs = this.customVisitorDocuments;
    const templates = this.requiredDocumentTemplates;

    return templates.map((template) => {
      const doc = docs.find((d) => d.belongsTo('userDocumentTemplate').id() === template.id);
      let statusLabel: string | null = null;
      if (doc) {
        if (doc.isApproved) statusLabel = 'Approved';
        else if (doc.isDenied) statusLabel = 'Denied';
        else statusLabel = 'Pending review';
      }
      return { template, isUploaded: !!doc, statusLabel };
    });
  }

  @action
  setActiveTab(tab: 'visit-details' | 'documents'): void {
    this.activeTab = tab;
  }

  @action
  async reloadDocuments(): Promise<void> {
    try {
      const include = this.abilities.can('access document-upload')
        ? 'user-document-template,user-document-links,user-document-attachments,user-document-attachments.user-document-template-attachment'
        : 'user-document-template,user-document-attachments,user-document-attachments.user-document-template-attachment';
      this.model.visitorDocuments = await this.store.query('visitor-document', {
        include,
        filter: { 'entry-id': this.model.id },
      });
    } catch (_) {
      // silent
    }
    void this.reloadVisitApprovalTask.perform();
  }

  reloadVisitApprovalTask = dropTask(async (): Promise<void> => {
    await this.store.findRecord('entry', this.model.id, {
      reload: true,
      include: 'visit-approval,visit-approval.screener-results,visit-approval.screener-reviews',
    });
  });

  /**
   * Reload the entry after the AVA approval card reports that data collection finished.
   * Mirrors the invite single-prereg `pollInviteReload`: the write is eventually-consistent,
   * so poll up to 5 times. Reloading refreshes the visit approval — unlocking the editable
   * fields once the approval resolves — and picks up any sign-in field values collected
   * during the approval.
   */
  pollEntryReloadTask = restartableTask(async (): Promise<void> => {
    const snapshot = JSON.stringify(this.model.userData ?? {});
    const snapshotNames = new Set(
      ((this.model.userData ?? A()) as UserDatum[])
        .map((datum) => datum.field)
        .filter((name): name is string => !!name),
    );

    for (let attempt = 0; attempt < 5; attempt++) {
      await timeout(zft(1000));

      try {
        await this.store.findRecord('entry', this.model.id, {
          reload: true,
          include: 'visit-approval,visit-approval.screener-results,visit-approval.screener-reviews',
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[pollEntryReload] reload failed, stopping poll', err);
        return;
      }

      const current = JSON.stringify(this.model.userData ?? {});
      if (current !== snapshot) {
        const newFieldNames = ((this.model.userData ?? A()) as UserDatum[])
          .map((datum) => datum.field)
          .filter((name): name is string => !!name && !snapshotNames.has(name));
        await this.refreshEntrySignInFieldsTask.perform(newFieldNames);
        return;
      }
    }
  });

  /**
   * After a reload reveals changed user data, rebuild the editable field changesets so they
   * re-render. When entirely new fields were collected, poll `entry-sign-in-field` until they
   * appear before rebuilding. Mirrors the invite `pollSignInFieldsRefresh`.
   */
  refreshEntrySignInFieldsTask = restartableTask(async (newFieldNames: string[]): Promise<void> => {
    if (newFieldNames.length === 0) {
      this._buildUserDataChangeset();
      return;
    }

    const includes = [
      'sign-in-field-actions',
      'actionable-sign-in-field-actions',
      'actionable-sign-in-fields',
      'flow-version',
    ].join(',');

    for (let attempt = 0; attempt < 5; attempt++) {
      let freshFields: NativeArray<SignInFieldModel>;
      try {
        freshFields = (await this.store.query('entry-sign-in-field', {
          entryId: this.model.id,
          include: includes,
        })) as unknown as NativeArray<SignInFieldModel>;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[refreshEntrySignInFields] query failed, stopping', err);
        return;
      }

      const freshNames = new Set(freshFields.map((field) => field.name));
      if (newFieldNames.every((name) => freshNames.has(name))) {
        this._routeSignInFields = freshFields;
        this._buildUserDataChangeset();
        return;
      }

      await timeout(zft(1000));
    }
  });

  // prettier-ignore
  @(employeesSearcherTask({
    filter: {
      deleted: false,
    },
    prefix: true,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  get backParams(): Record<string, string> {
    if (!this.model.signedInAt) return {};

    const startDate = formatISO(modifyDateInTimeZone(this.model.signedInAt, this.timezone, startOfDay));
    const endDate = formatISO(modifyDateInTimeZone(this.model.signedInAt, this.timezone, endOfDay));

    return { startDate, endDate };
  }

  get #returnToBackLink(): ReturnToBackLink | null {
    return resolveReturnToBackLink(this.returnTo, this.returnToModel);
  }

  get backLink(): string {
    return this.#returnToBackLink?.route ?? this.globalRouting.getBackLink(this.transitionedFrom, 'visitors.entries');
  }

  get backModels(): string[] {
    return this.#returnToBackLink?.models ?? [];
  }

  get backLinkQuery(): Record<string, string> {
    // A resolved returnTo origin (e.g. the profile Screeners tab) carries a tab
    // to restore; otherwise preserve the date-range the entries log expects.
    if (this.#returnToBackLink) {
      return this.returnToTab ? { tab: this.returnToTab } : {};
    }
    return this.backParams;
  }

  // Clear the sticky returnTo* params when navigating between sibling entries
  // via the Previous visits list. Without this, a stale returnTo carried from
  // the originating profile leaks onto the next entry, sending its Back button
  // to a profile the user never reached that entry from.
  get previousVisitsBackQuery(): Record<string, null> {
    return { returnTo: null, returnToModel: null, returnToTab: null };
  }

  @dependentKeyCompat
  get hasDirtyUserData(): boolean {
    return this.model.userData.isAny('isDirty') || this.isStaticDirty;
  }

  get cannotChangeFlows(): boolean {
    return (
      Boolean(this.model.anonymizedAt) ||
      this.model.needsApprovalReview ||
      this.abilities.cannot('edit entry') ||
      this.isANonAssignableFlow ||
      this.isMultiLocation ||
      this.featureFlags.isEnabled('disable-flow-change-on-existing-invites-and-entries') ||
      this.shouldDisableFlowForApprovedAvaEntry
    );
  }

  get shouldDisableFlowForApprovedAvaEntry(): boolean {
    if (!this.featureFlags.isEnabled('ava-invite-workflow-reruns')) return false;
    return this.model.approved && !!this.model.visitApproval?.visitWorkflowResult;
  }

  get isMultiLocation(): boolean {
    return this.locationNames.length > 1;
  }

  get locationNames(): string[] {
    return this.model.locationNames || [];
  }

  get primaryLocation(): string | undefined {
    return this.locationNames[0];
  }

  get secondaryLocations(): string[] {
    const [_, ...childLocationNames] = this.locationNames;
    return childLocationNames;
  }

  get isStaticDirty(): boolean {
    return Boolean(
      this.staticFields?.find((field) => {
        return (<DetailedChangeset<unknown>>field.changeset).isDirty;
      }),
    );
  }

  get endOfSignedOutMonth(): moment.Moment {
    return moment(this.model.signedOutAt).add(1, 'M').endOf('month');
  }

  get canPrintBadges(): boolean {
    const hasPrintPermission = this.authz.hasPermissionAtCurrentLocation(Permission.VISITORS_PRINTER_READ);

    return Boolean(hasPrintPermission && this.vrSubscription?.canEnableBadgePrinting);
  }

  get canSetPropertyNotes(): boolean {
    return this.locationIsConnectedToProperty;
  }

  get isANonAssignableFlow(): boolean {
    return this.model.isFromEmployeeScreening || this.model.isFromWalkUp;
  }

  get legalDocumentDescriptions(): Record<string, string> {
    return this.agreements.reduce<Record<string, string>>((obj, agreement) => {
      const agreeableNda = this.model.agreeableNdas.find((nda) => nda.agreement.id === agreement.id);
      /**
       * There may be agreeableNdas that have signed agreements (i.e. an "NDA" PDF) but that don't have a
       * relationship to an agreement (i.e., a legal document template) and therefore have no agreement name.
       * These signed agreements are from before visitor type flows existed. We assign them a generic
       * "Non-disclosure Agreement" name because NDAs used to be the only kind of agreement we supported.
       */
      const agreementName = typeof agreement.name === 'undefined' ? 'Non-disclosure Agreement' : agreement.name;

      if (agreeableNda?.signedAt) {
        const entrySignedInAt = moment(this.model.signedInAt);
        const agreementSignedAt = moment(agreeableNda?.signedAt);
        const wasPreviouslySigned = entrySignedInAt.diff(agreementSignedAt, 'days') !== 0;
        const description = wasPreviouslySigned ? `signed ${agreementSignedAt.format('MMM D, YYYY')}` : 'signed';

        obj[agreementName] = description;
      } else if (agreeableNda?.agreementOptional) {
        obj[agreementName] = 'optional';
      } else {
        obj[agreementName] = 'not signed';
      }

      return obj;
    }, {});
  }

  get locationIsConnectedToProperty(): boolean {
    return <number>this.connectedTenants.length > 0;
  }

  get isAdmin(): boolean {
    const roleNames = this.currentAdmin.roleNames;
    return isPresent(_intersection(['Global Admin', 'Location Admin', 'Front Desk Admin'], roleNames));
  }

  get thumbnailPath(): string {
    const largeThumbnail = this.model.thumbnails['large'];
    return largeThumbnail ? largeThumbnail : '/assets/images/features/avatar-placeholder.png';
  }

  get shouldShowQuestionnairesStatus(): boolean {
    if (!this.state.features?.canAccessQuestionnaires) return false;
    return (
      <number>this.model.flow?.content?.flowQuestionnaires.length > 0 ||
      <number>this.model.questionnaireResponses.length > 0
    );
  }

  get showSignOutSection(): boolean {
    const entry = this.model;

    // don't show "Signed Out" section if approval was denied and visitor never entered
    if (entry.approvalWasDenied) return false;

    // show "Signed Out" section is the entry is signed out, or the current user can edit the entry
    return !!entry.signedOutAt || this.abilities.can('edit entry', entry);
  }

  get canInviteAgain(): boolean {
    return (
      !this.model.anonymizedAt &&
      !this.model.isFromEmployeeScreening &&
      !this.model.isFromWalkUp &&
      !this.model.approvalWasDenied &&
      !this.model.needsApprovalReview
    );
  }

  _buildStaticFields(): void {
    const staticFields: MappedField[] = [];

    const fullNameObject = new MappedField({
      fieldData: this.model.userData.findBy('field', 'Your Full Name'),
      isRequired: true,
    });

    // eslint-disable-next-line ember/use-ember-get-and-set
    if (!isEmpty(this.currentLocation.config.get('localizedFields')) && fullNameObject.fieldData) {
      staticFields.push(fullNameObject);
    }

    const emailAddress = this.model.userData.findBy('field', 'Your Email Address');
    const phoneNumber = this.model.userData.findBy('field', 'Your Phone Number');

    if (emailAddress) {
      const decoratedEmail = new MappedField({ fieldData: emailAddress, isRequired: false });
      staticFields.push(decoratedEmail);
    }

    if (phoneNumber) {
      const decoratedPhone = new MappedField({ fieldData: phoneNumber, isRequired: false });

      staticFields.push(decoratedPhone);
    }

    this.model.userData.forEach((datum) => {
      if (!staticFields.find((field) => field.field === datum.field) && 'Purpose of visit' !== datum.field) {
        staticFields.push(new MappedField({ fieldData: datum, isRequired: false }));
      }
    });

    this.staticFields = staticFields;
  }

  get sourcesForApprovalReviewMetrics(): Record<string, boolean> {
    return (this.model.visitApproval?.failedResults ?? []).reduce<Record<string, boolean>>(
      (sources, report) => ({ ...sources, [report.screenerSource]: true }),
      {},
    );
  }

  /* MVT related
   * Keeping all the code related with MVT below
   */
  /*
   * flowField attempts to lookup an Entry's flow, first
   * by POV, then by the key given in config.localizedFields,
   * and finally by provided a POJO with the value of flowName
   *
   * TODO: What is the logic behind this? There's totally an
   * undefined case that's not covered here.
   */
  get flowField(): Partial<UserDatum> | UserDatum | undefined {
    const { model, currentLocation } = this;
    const { flowName, userData } = model;
    let povField: string | undefined = model.povKey;

    // This is only relevant on legacy entries.
    // After MVT all userData will be stored in English
    if (!povField) {
      povField = currentLocation.config
        // eslint-disable-next-line ember/use-ember-get-and-set
        .get('localizedFields')
        ?.find(({ field }) => field === 'Purpose of visit')?.localized;
    }

    let flowField: Partial<UserDatum> | UserDatum | undefined = userData.findBy('field', povField);

    if (!flowField && flowName) {
      // Notice POJO instead of ED object
      flowField = {
        value: flowName,
      };
    }

    return flowField;
  }

  /*
   * This is the current flow found on the entry, even if it does
   * not exist in `currentLocation.flows`
   */
  get currentFlowField(): Partial<FlowModel> {
    // Since flow and Purpose of Visit are completely different fields,
    // but the form element to change Purpose of visit relies on
    // currentLocation.flows, we have to make this composite structure
    // so that the entry's flow, as it is stored, is compatible with
    // the `currentLocation.flows`'s type

    let flowFieldJSON;
    if (this.flowField && !this.flowField.toJSON) {
      // This is when flowField returns as POJO
      flowFieldJSON = this.flowField;
    } else {
      // This is when flowField is an ED obj or UserDatum
      flowFieldJSON = this.flowField?.toJSON!();
    }
    let o;
    o = Object.assign({}, flowFieldJSON);
    o = Object.assign(o, { name: <string>this.flowField?.value });
    return o;
  }

  /*
   * All possible flows include all flows in `currentLocation.flows`
   * as well as any flow found on the entry.
   */
  get possibleFlowsForEntries(): NativeArray<FlowModel | AsyncBelongsTo<FlowModel> | Partial<FlowModel>> {
    const possibleFlowsForEntries = A<FlowModel | AsyncBelongsTo<FlowModel> | Partial<FlowModel>>();
    if (this.model.isFromEmployeeScreening || this.purposeOfVisit?.value === PURPOSE_OF_VISIT.EMPLOYEE_REGISTRATION) {
      possibleFlowsForEntries.push(this.model.flow);
      return possibleFlowsForEntries;
    }
    possibleFlowsForEntries.push(this.currentFlowField);
    this.currentLocation.flows.content
      .filter(({ employeeCentric, type, name }) => !NON_ASSIGNABLE_FLOWS.includes(type) && !employeeCentric && name)
      .forEach((f) => possibleFlowsForEntries.push(f));
    return possibleFlowsForEntries.uniqBy('name');
  }

  get hasFlowThatNoLongerExists(): boolean {
    const locationFlows = this.currentLocation.flows.content.filter(
      ({ employeeCentric, type }) => !NON_ASSIGNABLE_FLOWS.includes(type) && !employeeCentric,
    );
    return this.possibleFlowsForEntries.length > locationFlows.length;
  }

  get purposeOfVisit(): UserDatum | undefined {
    return this.model.userData.findBy('field', 'Purpose of visit');
  }

  /*
   * Flow is the full flow object, looked up from currentLocation,
   * that exists if it's found by name from what's listed on the entry
   */
  get flow(): AbstractFlowModel | undefined | null {
    // eslint-disable-next-line ember/no-get
    const flowId = <string>get(this.model, 'flow.id');
    const pov = <string>this.purposeOfVisit?.value;

    if (this.model.isFromEmployeeScreening || pov === PURPOSE_OF_VISIT.EMPLOYEE_REGISTRATION) {
      return this.currentLocation.employeeScreeningFlow;
    }

    // fallback on name instead of id if using non model based flow
    return (
      this.currentLocation.flows.content.find((flow) => flow.id === flowId) ??
      this.currentLocation.flows.content.find((flow) => flow.name === pov)
    );
  }

  get hasChanges(): boolean {
    const userDataChangesetModified = this.userDataChangesetModified;
    const hasDirtyAttributes = this.model.hasDirtyAttributes;
    const hasDirtyUserData = this.hasDirtyUserData;
    const hasDirtyVisitorDocuments = this.hasDirtyVisitorDocuments;

    return userDataChangesetModified || Boolean(hasDirtyAttributes) || hasDirtyUserData || hasDirtyVisitorDocuments;
  }

  get detailedResults(): ScreenerResult[] {
    return (
      this.model.visitApproval?.screenerResults.filter((result) => result.details && !result.isVisualCompliance()) ?? []
    );
  }

  @dependentKeyCompat
  get userDataChangesetModified(): boolean {
    return Boolean(this.userDataChangeset?.find((field) => field.isDirty));
  }

  get signInFields(): NativeArray<SignInFieldModel> | SignInFieldModel[] {
    if (this._routeSignInFields) {
      return this._routeSignInFields;
    }

    if (!this.flow) {
      return [];
    }

    // eslint-disable-next-line ember/no-get
    return (<AsyncHasMany<SignInFieldModel>>get(this.flow, 'signInFieldPage.signInFields')).content || [];
  }

  _buildUserDataChangeset(): void {
    const { model, currentLocation } = this;
    let signInFields: SignInFieldModel[] = [];
    const fields: Array<FieldChangeset> = [];

    if (this._routeSignInFields) {
      signInFields = this._routeSignInFields;
    } else if (this.flow) {
      // eslint-disable-next-line ember/no-get
      signInFields = (<AsyncHasMany<SignInFieldModel>>get(this.flow, 'signInFieldPage.signInFields')).content;
    }

    // bootstrap editable sign-in-fields based in entry.userData
    model.userData.forEach((userDatum, index) => {
      const { field: fieldName, value } = userDatum;
      /*
       * We ignore flow field, it is not editable via the
       * {{entry-fields}} component. If we allow flow edit, then
       * it will happen through a different interface in the template.
       */
      // eslint-disable-next-line ember/use-ember-get-and-set
      if (value !== this.flow?.get('name') && isPresent(signInFields)) {
        let field: Partial<SignInFieldModel> | SignInFieldModel | undefined = signInFields.find(
          (f) => f.name === fieldName,
        );

        if (!field) {
          /* The following conditions brute force a match for host, email or phone.
           *
           * In an ideal world we should have a found a match already, but:
           *
           *  - field for host changes (since it's customizable)
           *  - email can be saved on a different locale
           *  - phone number was store with the bdField (API regression)
           */
          if (value === model.host) {
            field = signInFields.find((f) => f.isHost);
          } else if (value === model.email) {
            field = signInFields.find((f) => f.isEmail);
          } else if (fieldName === 'phoneNumber') {
            field = signInFields.find((f) => f.isPhone);
          }
        }

        if (!field) {
          /*
           * If we get here, it means there used to be a
           * sign-in-field model for this field, but it was
           * removed.
           *
           * We create a "pseudo-field", so it displays as a
           * text-field in the template.
           *
           * Position 100 is to show the field at the bottom of
           * the list.
           */
          field = {
            id: `${index}-${currentLocation.id}`, // pseudo-id
            name: fieldName,
            isCustom: true,
            localized: fieldName,
            isLoaded: <Model['isLoaded']>(<unknown>true),
            position: 100,
          };
        }

        const fieldChangeset = <FieldChangeset>(<unknown>Changeset(ObjectProxy.create({ content: field, value })));
        fields.push(fieldChangeset);
      }
    });

    if (isPresent(signInFields)) {
      signInFields.forEach((field) => {
        // We want to prevent duplicate fields in the case where userData
        // also contains the same field
        const fieldIsAlreadyBootstrapped = !!fields.find((f) => f.id === field.id);
        const fieldIsGuestName = field.isGuestName;

        if (fieldIsGuestName || fieldIsAlreadyBootstrapped) {
          return;
        }

        fields.push(<FieldChangeset>(<unknown>Changeset(ObjectProxy.create({ content: field, value: '' }))));
      });
    }

    this.userDataChangeset = fields;
  }

  @action
  doSearch(term: string): TaskInstance<EmployeeModel[]> {
    return this.searchEmployeesTask.perform(term);
  }

  @action
  setHost(employee: EmployeeModel): void {
    this.model.host = employee && employee.name;
    this.model.employee = employee;
  }

  approveEntryTask = task(async (entry: EntryModel) => {
    try {
      await entry.approve();
      // eslint-disable-next-line ember/no-get
      await get(entry, 'flow.badge'); // load async relationship
      this.flashMessages.showAndHideFlash('success', 'Access approved', entryApprovalMessage(entry));
      await entry.reload();
      await refreshApprovalChainAuditLogs(this.logger);

      this.metrics.trackEvent('Dashboard Entry - Reviewed', {
        action: 'approve',
        entry_id: entry.id,
        source: 'Entry Details',
        ...this.sourcesForApprovalReviewMetrics,
      });
    } catch (error) {
      this.flashMessages.showAndHideFlash('error', 'Error approving entry', parseErrorForDisplay(error));
    }
  });

  denyEntryTask = task(async (entry: EntryModel) => {
    try {
      await entry.deny();
      this.flashMessages.showAndHideFlash('warning', 'Entry denied');
      await entry.reload();
      await refreshApprovalChainAuditLogs(this.logger);

      this.metrics.trackEvent('Dashboard Entry - Reviewed', {
        action: 'deny',
        entry_id: entry.id,
        source: 'Entry Details',
        ...this.sourcesForApprovalReviewMetrics,
      });
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Error denying entry');
    }
  });

  reprintBadgeTask = task(async (entry: EntryModel, printer: PrinterModel | null = null) => {
    try {
      let response;
      // FF. multiplePrinters
      if (printer) {
        const data = [{ type: 'entries', id: entry.id }];
        response = await printer.reprintBadge({ data });
      } else {
        // TODO Is this dead code?
        await entry.reprintBadge();
      }
      this._trackReprintBadgeRequested(entry.id);
      if (response) {
        const errored = response.data.filter((r) => r.attributes.result === 'error');
        if (errored.length) {
          const msg = 'Cannot print badge';
          const componentName = 'flash-message/printer-error';
          this.flashMessages.showFlashComponent('error', msg, componentName, response);
          const statuses = errored[0]?.attributes.statuses;
          this.metrics.trackEvent('Viewed Flash Message', {
            type: 'error',
            message_title: msg,
            message_codes: statuses,
          });
        } else {
          const msg = 'Printing badge!';
          this.flashMessages.showAndHideFlash('success', msg);
          this.metrics.trackEvent('Viewed Flash Message', {
            type: 'success',
            message_title: msg,
            message_codes: [],
          });
        }
      } else {
        this.flashMessages.showAndHideFlash('success', 'Printing badge!');
      }
    } catch (_e) {
      this.flashMessages.showAndHideFlash('error', 'Cannot print badge');
    }
  });

  _trackReprintBadgeRequested(entryId: string): void {
    const entry_id = parseInt(entryId, 10);
    const properties = {
      action_origin: 'entry_page',
      entry_id,
    };
    this.metrics.trackEvent('Reprint Entry Badge Requested', properties);
  }

  @dropTask
  showDeleteConfirmationTask: {
    entries: EntryModel[];
    abort?(): void;
    continue?(args: EntryModel[]): void;
    perform(entries: EntryModel[]): Generator<Promise<unknown>, unknown, unknown>;
  } = {
    entries: [],
    *perform(entries: EntryModel[]): Generator<Promise<unknown>, unknown, unknown> {
      this.entries = entries;

      const deferred = defer();

      this.abort = () => deferred.reject();
      this.continue = (args) => deferred.resolve(args);
      return yield deferred.promise;
    },
  };

  saveTask = task(async () => {
    const { model } = this;
    const { visitorDocuments } = model;
    const token = isTesting ? testWaiter.beginAsync() : false;

    this.updateAllUserData(model);

    await model.save();
    try {
      await all(
        (visitorDocuments ?? []).map((visitorDocument) => this.saveVisitorDocumentTask.perform(model, visitorDocument)),
      );
    } catch (_error) {
      this.flashMessages.showFlash('error', 'Visitor saved! But one or more document uploads failed');

      return;
    } finally {
      if (token) testWaiter.endAsync(token);
    }

    this.flashMessages.showAndHideFlash('success', 'Saved!');
  });

  loadEventReports = dropTask(async () => {
    const location = this.currentLocation.id;
    const identifier = `vr:entry:${this.model.id}`;
    try {
      /**
       * We're querying for both event-reports and host-notification platform-jobs,
       * because eventually we will deprecate event-reports.
       * For now, we can de-dupe them by replacing any event-reports that have a corresponding
       * platform-job with the same name.
       */
      const platformJobsQueryResult = await this.store.query('platform-job', { filter: { identifier } });
      const newPlatformJobs = <EventReportModel[]>(
        platformJobsQueryResult.slice().filter((job) => job.pluginCategory === 'host-notifications')
      );
      const platformJobNames = newPlatformJobs.map((job) => job.name);
      const newEventReportsQueryResult = await this.store.query('event-report', { filter: { identifier, location } });
      const newEventReports = newEventReportsQueryResult.filter((report) => !platformJobNames.includes(report.name));

      newEventReports.push(...newPlatformJobs);

      if (newEventReports.length) {
        const loadedReports = this.eventReports;
        const newEventIds = newEventReports.map((report) => report.sourceId);

        // Since it's possible that we could have an old event report in "pending" status that has since
        // changed to a "terminal" status, we always take the latest version of any duplicate event reports
        const updatedEventReportList = loadedReports
          ?.filter((report) => !newEventIds.includes(report.sourceId))
          .concat(newEventReports);

        this.eventReports = updatedEventReportList;
      }
      return newEventReports;
    } catch (e) {
      this.flashMessages.showFlash('error', 'Error loading notification statuses', parseErrorForDisplay(e));
      throw e;
    }
  });

  pollEventReports = restartableTask(async () => {
    if (!this.currentLocation.hostNotificationsEnabled) {
      return;
    }

    const location = this.currentLocation.id;
    let keepPolling = false;
    let maxPolls = NUMBER_OF_POLLING_TRIES;
    do {
      if (isBlank(location)) {
        break;
      }
      const newEventReports = await this.loadEventReports.perform();
      // Only poll as long as there are event reports that are either `queued` or `inProgress`.
      const hasPendingEvents = newEventReports.some((event) => event.inProgress || event.queued);
      // The events creation for a new entry is async, so the intial request for a just created entry
      // could be empty, so we give `NUMBER_OF_POLLING_TRIES`
      maxPolls--;

      keepPolling = hasPendingEvents || maxPolls > 1;
      await timeout(EVENT_REPORT_POLLING_TIMEOUT);
    } while ((config.environment !== 'test' || config.testLongPolling) && keepPolling);
  });

  rollbackChanges(): void {
    const { model } = this;
    const userDataChangeset = this.userDataChangeset;

    model.rollbackAttributes();
    userDataChangeset?.forEach((changeset) => changeset.rollback());
    model.visitorDocuments.invoke('rollbackAttributes');
  }

  @action
  setAdditionalHosts(hosts: EmployeeModel[]): void {
    this.model.additionalHosts = hosts;
  }

  @action
  setHostAndUserData(changeset: FieldChangeset, employee: EmployeeModel): void {
    this.setHost(employee);
    set(changeset, 'value', employee.name);
  }

  updateAllUserData(entry: EntryModel): void {
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.beginPropertyChanges();
    this.userDataChangeset
      ?.filter((changeset) => changeset.isDirty)
      .forEach((fieldChangeset) => {
        addOrUpdateUserData(entry, fieldChangeset.name, fieldChangeset.value);

        fieldChangeset.execute();
        fieldChangeset.rollback();
      });

    this.staticFields
      ?.filter((field) => field.changeset?.isDirty)
      .forEach((field) => {
        field.changeset?.execute();
        field.changeset?.rollback();
      });
    // @ts-ignore private function
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.endPropertyChanges();
  }

  loadPreviousEntries = task(async (url?: string) => {
    if (isBlank(url) && this.model.id) {
      url = urlBuilder.v3.entry.previousEntriesUrl(this.model.id);
    }
    const result = await this.ajaxFetch.request<{ links: { next: string } }>(url!, {
      headers: { accept: 'application/vnd.api+json' },
      contentType: 'application/vnd.api+json',
      data: { page: { limit: 3 } },
    });

    const entries = normalizeResponse(this.store, 'entry', result);

    const previousEntries = <Enumerable<EntryModel>>(<unknown>this.store.push(entries));
    this.previousEntries.addObjects(previousEntries);

    this.morePreviousEntriesUrl = result.links.next;
  });

  @action
  setFlow(flow: FlowModel): void {
    this.model.flowName = flow.name;
    this.flowField!.value = flow.name;
    // Only assign a model to the rel.
    // Need to reset rel so fallback on virtual flow looks up proper flow.
    // Will get fixed on save when proper flow is returned from API
    // @ts-ignore
    set(this.model, 'flow', flow.id ? flow : undefined);
    // Changing the flow invalidates version-pinned fields — clear so
    // _buildUserDataChangeset uses the newly selected flow's fields instead.
    this._routeSignInFields = null;
    this._buildUserDataChangeset();
  }

  @action
  save(): void {
    void this.saveTask.perform();
  }

  @action
  delete(): void {
    (<TaskInstance<void>>(<unknown>this.showDeleteConfirmationTask.perform([this.model]))).then(
      () => {
        this.router.transitionTo(this.globalRouting.getBackLink(this.transitionedFrom, 'visitors.entries'), {
          queryParams: this.backParams,
        });
      },
      (reason: Error) => {
        if (reason && reason.message !== 'TransitionAborted') {
          throw reason;
        }
      },
    );
  }

  @action
  async deletePhoto(): Promise<void> {
    try {
      this.model.deletePhoto = true;
      await this.model.save();

      this.flashMessages.showAndHideFlash('success', 'Visitor photo deleted');
    } catch (_error) {
      this.flashMessages.showFlash('error', 'Unable to delete visitor photo', { details: 'Please try again' });
    }
  }

  @action
  reprintBadge(entry: EntryModel, printer = null): void {
    void this.reprintBadgeTask.perform(entry, printer);
  }

  @action
  signOut(time: string): void {
    const signInDateFormatted = moment(this.model.signInTime).format('YYYY-MM-DD');
    const signOutTime = time ? moment(`${signInDateFormatted} ${time}`, 'YYYY-MM-DD h:mm a').toDate() : new Date();
    // we need to do this because we set signOutTime here, and again in the sign-out-entry
    // and Ember will throw an error because we're hammering this value
    this.model._signOutTime = signOutTime;
    (<TaskInstance<void>>(<unknown>this.showSignOutConfirmationTask.perform([this.model]))).then(
      () => {
        return this.router.transitionTo(this.globalRouting.getBackLink(this.transitionedFrom, 'visitors.entries'), {
          queryParams: this.backParams,
        });
      },
      (reason: Error) => {
        if (reason && reason.message !== 'TransitionAborted') {
          throw reason;
        } else {
          this.model.rollbackAttributes();
        }
      },
    );
  }

  @action
  checkForAttachments(): void {
    this.hasAttachments = (this.visitorDocuments ?? []).some(
      (visitorDocument) => visitorDocument.hasAttachedFile || visitorDocument.hasInputFieldData,
    );
  }

  // Manage Visitor Documents

  @cached
  get visitorDocuments(): VisitorDocumentModel[] {
    const { model, activeUserDocumentTemplateConfigurations } = this;

    return (activeUserDocumentTemplateConfigurations?.content ?? [])
      .toSorted((a, b) => a.userDocumentTemplate.content!.position - b.userDocumentTemplate.content!.position)
      .map((config) => {
        const { userDocumentTemplate } = config;

        return (
          model.visitorDocumentForTemplate(userDocumentTemplate) ||
          this.store.createRecord('visitor-document', { userDocumentTemplate: userDocumentTemplate.content })
        );
      });
  }

  get hasDirtyVisitorDocuments(): boolean {
    return this.visitorDocuments.some((visitorDocument) => this.isVisitorDocumentPersistable(visitorDocument));
  }

  get isValidVisitorDocuments(): boolean {
    const documentsToCheck = this.visitorDocuments?.filter((visitorDocument) => visitorDocument.hasAttachedFile);
    if (isEmpty(documentsToCheck)) return true;

    return documentsToCheck.every((visitorDocument) => visitorDocument.isValidDocument);
  }

  get modalElement(): HTMLElement | null {
    return document.getElementById('modal');
  }

  isVisitorDocumentPersistable(visitorDocument: VisitorDocumentModel): boolean {
    const activeIdentifiers = this.activeUserDocumentTemplateConfigurations.content.map(
      (config) => config.userDocumentTemplate.content?.identifier,
    );
    const isAssociatedToActiveTemplate = activeIdentifiers.includes(visitorDocument.identifier);
    const hasAttachmentsWithPendingUploads = visitorDocument.userDocumentAttachmentsPendingUpload.length > 0;
    const hasDirtyAttributes = <boolean>(<unknown>visitorDocument.hasDirtyAttributes) && !visitorDocument.isNew;

    return isAssociatedToActiveTemplate && (hasAttachmentsWithPendingUploads || hasDirtyAttributes);
  }

  @action
  attachFileToDocument(
    visitorDocument: VisitorDocumentModel,
    userDocumentTemplateAttachment: UserDocumentTemplateAttachmentModel,
    update: File | string,
  ): void {
    visitorDocument.entries.content.push(this.model);

    let userDocumentAttachment = visitorDocument.getAttachment(userDocumentTemplateAttachment.id);

    if (!userDocumentAttachment) {
      userDocumentAttachment = this.store.createRecord('user-document-attachment');
      userDocumentAttachment.userDocumentTemplateAttachment = userDocumentTemplateAttachment;
      userDocumentAttachment.visitorDocument = visitorDocument;
    }

    if (update instanceof File) {
      userDocumentAttachment.file = update;
    }

    if (typeof update === 'string') {
      userDocumentAttachment.fileUrl = update;
    }
  }

  @action
  resetVisitorDocument(visitorDocument: VisitorDocumentModel): void {
    const { model } = this;
    const { userDocumentTemplate } = visitorDocument;

    if (<boolean>(<unknown>visitorDocument.isNew)) {
      model.visitorDocuments.removeObject(visitorDocument);
      visitorDocument.unloadRecord();
      model.visitorDocuments.addObject(
        this.store.createRecord('visitor-document', { userDocumentTemplate: userDocumentTemplate.content }),
      );
    } else {
      this.confirmRemovalOfDocument = visitorDocument;
    }
  }

  @action
  inviteAgain(entry: EntryModel): void {
    const { email, fullName: name } = entry;
    const previousInvite = entry.belongsTo('invite').id();

    void this.router.transitionTo('visitors.invites.new', {
      queryParams: { email, name, previousInvite, returnTo: 'visitors.entries' },
    });

    this.metrics.trackEvent('Entries Invited Again', { invite_id: previousInvite });
  }

  @action
  createBlocklistFilter(): void {
    this.blocklistFilterInModal = createBlocklistFilter({
      model: this.model,
      store: this.store,
      location: this.state.currentLocation,
    });

    this.metrics.startJob();

    this.metrics.trackJobEvent('Invite - Add to Block List Button Clicked', {
      entry_id: this.model.id,
    });
  }

  saveBlocklistFilterTask = task(
    waitFor(async (blocklistFilter: BlacklistFilter, photo: File | null) => {
      try {
        await saveBlocklistFilter({
          blocklistFilter,
          failOnPhotoFetchError: this.featureFlags.isEnabled('blocklist-fail-on-photo-fetch-fail'),
          photo,
          uploaderFactory: this.uploaderFactory,
        });
        this.blocklistFilterInModal = undefined;
        this.flashMessages.showAndHideFlash('success', 'Blocklist filter saved');
      } catch (e) {
        this.flashMessages.showFlash('error', parseErrorForDisplay(e));
      }
    }),
  );

  @dropTask
  showSignOutConfirmationTask: {
    entries: EntryModel[];
    continue?(): void;
    abort?(): void;
    perform(entries: EntryModel[]): Generator<Promise<unknown>, void, void>;
  } = {
    entries: [],

    *perform(entries: EntryModel[]): Generator<Promise<unknown>, void, void> {
      this.entries = entries;

      const deferred = defer();

      this.abort = () => deferred.reject();
      this.continue = () => deferred.resolve(true);

      return yield deferred.promise;
    },
  };

  saveVisitorDocumentTask = task(async (entry: EntryModel, visitorDocument: VisitorDocumentModel) => {
    if (!this.isVisitorDocumentPersistable(visitorDocument)) return visitorDocument;

    // Associate the entry's location to the visitor document (fallback to current location)
    const location = this.store.peekRecord('location', entry.belongsTo('location').id()) || this.state.currentLocation;

    visitorDocument.locations = [location];

    await this.uploadVisitorDocumentAttachmentsTask.perform(entry, visitorDocument);
    await visitorDocument.save();

    // Cleanup attachments
    visitorDocument.userDocumentAttachments.content
      .filter((attachment) => attachment.isNew)
      .map((attachment) => attachment.unloadRecord());

    return visitorDocument;
  });

  uploadVisitorDocumentAttachmentsTask = task(async (entry: EntryModel, visitorDocument: VisitorDocumentModel) => {
    if (!visitorDocument.userDocumentAttachmentsPendingUpload.length) return;

    const { activeStorageExtension } = this;
    const directUploadURL = '/a/visitors/api/direct-uploads';
    const userDocumentTemplateId = visitorDocument.belongsTo('userDocumentTemplate').id();

    if (!(entry?.id && userDocumentTemplateId)) return;

    const prefix = `user-documents/${userDocumentTemplateId}/entries/${entry.id}`;
    const endpoint = `${directUploadURL}?prefix=${prefix}`;

    return await all(
      visitorDocument.userDocumentAttachmentsPendingUpload.map((userDocumentAttachment) =>
        this.uploadDocumentAttachmentTask.perform(userDocumentAttachment, endpoint, activeStorageExtension),
      ),
    );
  });

  @task uploadDocumentAttachmentTask: {
    progress: number;
    perform(
      userDocumentAttachment: UserDocumentAttachmentModel,
      endpoint: string,
      activeStorageExtension: ActiveStorageService,
    ): Generator<Promise<{ signedId: string }>, unknown, { signedId: string }>;
  } = {
    progress: 0,

    *perform(
      userDocumentAttachment: UserDocumentAttachmentModel,
      endpoint: string,
      activeStorageExtension: ActiveStorageService,
    ): Generator<Promise<{ signedId: string }>, unknown, { signedId: string }> {
      const { file } = userDocumentAttachment;

      if (!(file instanceof File)) {
        throw new Error('Upload halted: no file specified');
      }

      if (!endpoint) {
        throw new Error('Upload halted: no direct upload endpoint specified');
      }

      if (typeof activeStorageExtension.upload !== 'function') {
        throw new Error('Upload halted: invalid Active Storage service specified');
      }

      const { signedId } = yield activeStorageExtension.upload(file, endpoint, {
        onProgress: (progress) => {
          this.progress = progress;
          if (!userDocumentAttachment.isDestroyed && !userDocumentAttachment.isDestroying) {
            set(userDocumentAttachment, 'uploadProgress', progress);
          }
        },
      });

      userDocumentAttachment.file = signedId;

      return signedId;
    },
  };

  // Dynamic import extracted so tests can override this to provide a fake
  // component without relying on webpack's module-federation chunk loader
  // (which doesn't resolve in the ember test env).
  async loadApprovalChainDetailsCard(): Promise<typeof ApprovalChainDetailsCard> {
    const module = await import('reactRichComponents/visitors/ApprovalChainDetailsCard');
    return module.ApprovalChainDetailsCard;
  }

  @action
  async injectApprovalChainDetailsCard(): Promise<void> {
    this.destroyApprovalChainDetailsCard();

    const ApprovalChainDetailsCard = await this.loadApprovalChainDetailsCard();

    const inviteId = this.model.belongsTo('invite').id();
    const isWalkup = !inviteId;

    if (!document.getElementById('react__approval-chain-details-card')) {
      return;
    }

    this.approvalChainDetailsCardRoot = await this.reactRenderer.render(
      ApprovalChainDetailsCard,
      'react__approval-chain-details-card',
      {
        ...(inviteId ? { inviteId } : {}),
        employeeId: this.currentAdmin.id,
        readOnly: !isWalkup && !this.featureFlags.isEnabled('ava-invite-workflow-reruns'),
        entryId: this.model.id,
        visitModified: Boolean(
          this.model.visitApproval?.employeeTriggeredRescreen || this.model.visitApproval?.visitorTriggeredRescreen,
        ),
        onDataCollectionComplete: () => void this.pollEntryReloadTask.perform(),
      },
    );
  }

  @action
  destroyApprovalChainDetailsCard(): void {
    void this.pollEntryReloadTask.cancelAll();
    void this.refreshEntrySignInFieldsTask.cancelAll();
    if (this.approvalChainDetailsCardRoot) {
      this.reactRenderer.unmount(this.approvalChainDetailsCardRoot);
      this.approvalChainDetailsCardRoot = undefined;
    }
  }

  removeVisitorDocumentFromEntryTask = task(async (visitorDocument: VisitorDocumentModel, entry: EntryModel) => {
    const { userDocumentTemplate } = visitorDocument;

    try {
      await visitorDocument.removeFromEntries([entry]);
      entry.visitorDocuments.removeObject(visitorDocument);
      entry.visitorDocuments.addObject(
        this.store.createRecord('visitor-document', { userDocumentTemplate: userDocumentTemplate.content }),
      );
      this.flashMessages.showAndHideFlash('success', `${visitorDocument.title} removed from entry`);
    } catch (_) {
      entry.visitorDocuments.removeObject(entry.visitorDocumentForTemplate(userDocumentTemplate)!);
      entry.visitorDocuments.addObject(visitorDocument);
      this.flashMessages.showFlash('error', `Failed to remove ${visitorDocument.title} from entry`);
    }
  });
}
